<template>
  <div class="area-search">
    <div class="search__breadcrumbs-wrapper">
      <Breadcrumbs
        :currentContext="currentContext"
        :currentRefinements="currentRefinements"
        :filterMap="filterMap"
        :searchQuery="searchQuery"
        @removeFilter="removeFilter"
      />
    </div>
    <div class="area-search__toggles-wrapper">
      <Toggles @updateViewStyle="updateViewStyle" :viewStyle="viewStyle" />
    </div>
    <div class="area-search__filters-wrapper">
      <Filters
        :currentContext="currentContext"
        :features="features"
        :filters="currentFilters"
        :filterMap="filterMap"
        :currentRefinedValues="currentRefinedValues"
        :isFreeTrialUser="isFreeTrialUser"
        @updateFilters="updateFilters"
      />
    </div>
    <div class="area-search__results-wrapper">
      <div class="area-search__context-wrapper">
        <Context
          :contexts="availableContexts"
          :currentContext="currentContext"
          :itemsLength="itemsLength"
          @updateContext="updateContext"
        />
      </div>
      <template v-if="!noResultsFound">
        <TileResults
          v-if="viewStyle === 'tile'"
          :items="currentItems"
          :contentType="currentContext"
          :isFreeTrialUser="isFreeTrialUser"
          @openVideo="openVideo"
        />
        <ListResults
          v-else
          :items="currentItems"
          :contentType="currentContext"
          :isFreeTrialUser="isFreeTrialUser"
          @openVideo="openVideo"
        />
      </template>
      <div v-else class="area-search__no-results-wrapper">
        <h6 class="area-search__no-results">No Results Found</h6>
      </div>
    </div>
  </div>
</template>

<script>
import { createIndices, toggleFacet, removeFacet } from '@/services/search'
import Filters from '@/components/search/Filters.vue'
import TileResults from '@/components/search/TileResults.vue'
import ListResults from '@/components/search/ListResults.vue'
import Context from '@/components/search/Context.vue'
import Toggles from '@/components/search/Toggles.vue'
import Breadcrumbs from '@/components/search/Breadcrumbs.vue'

export default {
  name: 'AreaSearch',
  components: {
    Filters,
    TileResults,
    ListResults,
    Context,
    Toggles,
    Breadcrumbs,
  },
  props: {
    learningArea: {
      type: String,
      required: true,
    },
    algoliaKeys: {
      type: Object,
      required: true,
    },
    searchQuery: {
      type: String,
      required: true,
    },
    filterMap: {
      type: Object,
      required: true,
    },
    isAuthenticated: {
      type: Boolean,
      required: true,
    },
    isShowingLearningPaths: {
      type: Boolean,
      required: true,
    },
    isFreeTrialUser: {
      type: Boolean,
      required: true,
    },
    facets: {
      type: Object,
      required: true,
    },
    features: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      contexts: ['Courses', 'Learning Paths'],
      hasReachedBottom: false,
      searchInstance: {},
      indices: {
        Courses: {},
        'Learning Paths': {},
      },
      searchResults: {
        Courses: [],
        'Learning Paths': [],
      },
      pages: {
        Courses: {
          currentPage: 0,
          totalPages: 0,
        },
        'Learning Paths': {
          currentPage: 0,
          totalPages: 0,
        },
      },
      facetResults: {
        Courses: {},
        'Learning Paths': {},
      },
      currentRefinements: {
        Courses: {},
        'Learning Paths': {},
      },
      currentContext: 'Courses',
      viewStyle: 'tile',
    }
  },
  computed: {
    currentItems() {
      return this.searchResults[this.currentContext]
    },
    itemsLength() {
      return {
        Courses: this.searchResults.Courses.length,
        'Learning Paths': this.searchResults['Learning Paths'].length,
      }
    },
    currentFilters() {
      return this.facetResults[this.currentContext]
    },
    noResultsFound() {
      return this.itemsLength[this.currentContext] === 0
    },
    currentRefinedValues() {
      return Object.entries(this.currentRefinements[this.currentContext]).map(
        (value, _) => value
      )
    },
    availableContexts() {
      const contexts = ['Courses']
      if (this.isShowingLearningPaths) {
        contexts.push('Learning Paths')
      }
      return contexts
    },
  },
  watch: {
    searchQuery(query) {
      this.handleSearchQuery(query)
    },
    hasReachedBottom(bottom) {
      if (bottom) {
        this.loadMore()
      }
    },
  },
  methods: {
    openVideo() {
      return null
    },
    updateViewStyle(style) {
      this.viewStyle = style
    },
    updateContext(context) {
      this.currentContext = context
    },
    isBottomVisible() {
      const scrollY = window.scrollY
      const visible = document.documentElement.clientHeight
      const pageHeight = document.documentElement.scrollHeight
      const bottomOfPage = visible + scrollY >= pageHeight - 1000
      return bottomOfPage || pageHeight < visible
    },
    loadMore() {
      const { currentPage, totalPages } = this.pages[this.currentContext]
      if (currentPage !== totalPages) {
        this.indices[this.currentContext].nextPage()
        this.indices[this.currentContext].search()
      }
    },
    handleResults(index, { hits, facets, page, nbPages }) {
      this.facetResults[index] = facets
      this.pages[index].currentPage = page
      this.pages[index].totalPages = nbPages
      if (this.pages[index].currentPage === 0) {
        this.searchResults[index] = hits
      } else {
        this.searchResults[index] = [...this.searchResults[index], ...hits]
      }
    },
    handleSearchQuery(query) {
      this.contexts.map(index => {
        this.indices[index].setQuery(query)
        this.indices[index].search()
      })
    },
    handleUpdates(index, { facetsRefinements, page }) {
      this.currentRefinements[index] = facetsRefinements
      this.pages[index].currentPage = page
    },
    updateFilters({ facet, value }) {
      toggleFacet(this.indices[this.currentContext], { facet, value })
    },
    removeFilter({ facet, value }) {
      removeFacet(this.indices[this.currentContext], { facet, value })
    },
    createSearchInstance() {
      const {
        algoliaKeys: {
          appKey,
          appId,
          coursesIndex: courses,
          learningPathsIndex: learningPaths,
        },
      } = this
      const setup = {
        keys: {
          appId,
          appKey,
        },
        contexts: [
          {
            name: 'Courses',
            index: courses,
            facets: this.facets.Courses,
          },
          {
            name: 'Learning Paths',
            index: learningPaths,
            facets: this.facets['Learning Paths'],
          },
        ],
        resultsHandler: this.handleResults,
        updatesHandler: this.handleUpdates,
      }
      const parentFilter = {
        facet: 'learning_areas.name',
        value: this.learningArea,
      }
      const { instance, indices } = createIndices(setup, parentFilter)
      this.searchInstance = instance
      this.indices = indices
    },
  },
  created() {
    window.addEventListener('scroll', () => {
      this.hasReachedBottom = this.isBottomVisible()
    })
    this.createSearchInstance()
  },
  destroyed() {
    window.removeEventListener('scroll', () => null)
  },
}
</script>

<style lang="scss" scoped>
.area-search {
  background-color: var(--dark-gray);
  min-height: 100vh;
  max-width: 1900px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  position: relative;
  &__breadcrumbs-wrapper {
    width: 100%;
    display: none;
    @include desktop {
      display: block;
    }
  }
  &__toggles-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    border-bottom: 1px solid var(--flat-black);
    padding: 0 0 12.4px 0;
    margin: 25px 0;
  }
  &__filters-wrapper {
    width: 100%;
    padding: 0 0 50px 0;
    @include tablet {
      width: 25%;
      padding: 5px 5px 0 5px;
    }
  }
  &__context-wrapper {
    margin: 0 0 20px 0;
    padding: 0 0 0 5px;
    @include tablet {
      padding: 0 0 0 15px;
    }
  }
  &__results-wrapper,
  &__no-results-wrapper {
    width: 100%;
    min-height: 100vh;
    @include tablet {
      width: 75%;
      padding: 5px;
    }
  }
  &__no-results {
    width: 100%;
    color: var(--white);
    font-size: 1.5rem;
    text-align: center;
    margin: 0 auto;
    text-transform: uppercase;
  }
}
</style>
